import { useForm, Controller, useWatch, set } from "react-hook-form";
import UpdateInterviewRequestModel from "../../../../models/admin/interviews/request/UpdateInterviewRequestModel";
import IdNameModel from "../../../../models/common/IdNameModel";
import toasterUtils from "../../../../utils/toaster-utils";
import { FORM_SELECT_RULE } from "../../../../utils/validation-utils";
import { GuidEnum } from "../../../../models/enums/GuidEnum";
import { Autocomplete, Box, Button, FormControl, Grid, Link, TextField, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import adminInterviewsService from "../../../../services/admin/admin-interviews-service";
import dateTimeUtils from "../../../../utils/date-utils";
import { useState } from "react";
import InterviewerSchedulesModel from "../../../../models/interviewer/response/InterviewerSchedulesModel";
import InterviewerDetailsForRescheduleModel from "../../../../models/admin/interviews/response/InterviewerDetailsForRescheduleModel";

interface UpdateInterviewProps {
    objInterview: UpdateInterviewRequestModel;
    interviewerList: InterviewerDetailsForRescheduleModel[];
    timeSlotList: IdNameModel[];
    primarySkillId: number;
    onRescheduleInterviewAction: (objInterview: UpdateInterviewRequestModel) => void;
    onCloseUpdateInterview: () => void;
};

const UpdateInterviewComponent = (props: UpdateInterviewProps) => {
    const [interviewerSchedules, setInterviewerSchedules] = useState<InterviewerDetailsForRescheduleModel[]>(props.interviewerList);
    const [selectedInterviewer, setSelectedInterviewer] = useState<string>("");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<number>(0);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<UpdateInterviewRequestModel>({
        mode: "onChange",
        defaultValues: {
            ...props.objInterview,
            interviewDate: props.objInterview.interviewDate
                ? (dayjs(props.objInterview.interviewDate) as any)
                : null,
        },
    });

    const onSubmit = (formData: UpdateInterviewRequestModel) => {
        if (selectedInterviewer === "" || selectedTimeSlot === 0) {
            toasterUtils.showError("Please select time slot.");
            return;
        }
        if (formData.interviewDate === null) {
            toasterUtils.showError("Interview date is mandatory.");
        } else {
            //formData.interviewDate = dateTimeUtils.addDays(interviewDate, 1);
            formData.interviewerId = selectedInterviewer;
            formData.timeSlotId = selectedTimeSlot;
            props.onRescheduleInterviewAction(formData);
        }
    };

    const selectSlot = (timeSlotId: number, interviewerId: string) => {
        setSelectedTimeSlot(timeSlotId);
        setSelectedInterviewer(interviewerId);
    };

    const clearSelectedSlot = () => {
        setSelectedTimeSlot(0);
        setSelectedInterviewer("");
    };

    const getInterviewerSchedules = (date: Date | null) => {
        if (date !== null) {
            setInterviewerSchedules([]);
            clearSelectedSlot();
            let dateString: string = dateTimeUtils.getFormattedDate(date);
            adminInterviewsService.getInterviewersByPrimarySkillForViewInterviews(props.primarySkillId, props.objInterview.interviewId, dateString).then((res) => {
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                setInterviewerSchedules(res.items);
            }).catch((error) => {
                toasterUtils.showError(error);
                setInterviewerSchedules([]);
            });
        }
    };

    return (
        <div className="card card-white">
            <div className="card-body">
                <form className="contact quform" onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                        <div className="col-md-6">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller name="interviewDate" control={control} rules={{ required: "Date is required" }}
                                    render={({ field, fieldState }) => (
                                        <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                                            <DatePicker
                                                label="Interview Date"
                                                value={field.value ? dayjs(field.value) : null}
                                                onChange={(newDate) => {
                                                    field.onChange(newDate);
                                                    getInterviewerSchedules(newDate ? newDate?.toDate() : null);
                                                }}
                                                format="YYYY-MMM-DD"
                                                minDate={dayjs()}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        error: !!fieldState.error,
                                                        helperText: fieldState.error?.message,
                                                        onKeyDown: (e) => e.preventDefault(),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>
                    {(selectedInterviewer === "" || selectedTimeSlot === 0) &&
                        <div className="row">
                            <div className="col-md-12">
                                {interviewerSchedules.map((interviewer) => (
                                    <Box mb={4}>
                                        <Typography variant="h6" className="interviewer-name-style">
                                            {interviewer.interviewerName}
                                        </Typography>
                                        <Grid container spacing={1} className="interviewer-slots-container">
                                            {interviewer.availableTimeSlots.map((slot, index) => (
                                                <Grid item key={index}>
                                                    <Link href="#" className={`interviewer-slots-style ${slot.isAvailable ? "available-slot" : ""}`}
                                                        onClick={(e) => selectSlot(slot.timeSlotId, interviewer.interviewerId)}>
                                                        {slot.timeSlotName}
                                                    </Link>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ))}
                            </div>
                        </div>
                    }
                    {selectedInterviewer !== "" && selectedTimeSlot !== 0 &&
                        <>
                            <div className="row">
                                <div className="col-md-4">
                                    <Typography variant="subtitle1">Selected Interviewer :</Typography>
                                </div>
                                <div className="col-md-6">
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {props.interviewerList.find(ts => ts.interviewerId === selectedInterviewer)?.interviewerName}
                                    </Typography>
                                </div>
                                <div className="col-md-4">
                                    <Typography variant="subtitle1">Selected Time Slot :</Typography>
                                </div>
                                <div className="col-md-6">
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {props.timeSlotList.find(ts => ts.id === selectedTimeSlot)?.name}
                                    </Typography>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <Link href="#" className="choose-another-slot-link-style"
                                        onClick={(e) => clearSelectedSlot()}>
                                        Choose another slot
                                    </Link>
                                </div>
                                <div className="col-md-6">
                                    <Button variant="contained" color="success" type="submit">
                                        Update
                                    </Button>
                                    <Button variant="contained" color="error" className="m-l-xxs" type="button" onClick={props.onCloseUpdateInterview}>
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </>
                    }
                </form>
            </div>
        </div>
    );
};
export default UpdateInterviewComponent;